import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  Input,
  HostListener,
  NgZone
} from '@angular/core';
import * as THREE from 'three';

// ─── 顶点着色器 ─────────────────────────────────────────────────────────────
const vertexShader = `
attribute vec3 position;
attribute vec2 uv;

varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position, 1.0);
}
`;

// ─── 片元着色器 ─────────────────────────────────────────────────────────────
const fragmentShader = `
precision highp float;

uniform float uTime;
uniform vec3 uColor;
uniform vec3 uResolution;
uniform vec2 uMouse;
uniform float uAmplitude;
uniform float uSpeed;

varying vec2 vUv;

void main() {
  float mr = min(uResolution.x, uResolution.y);
  vec2 uv = (vUv.xy * 2.0 - 1.0) * uResolution.xy / mr;

  uv += (uMouse - vec2(0.5)) * uAmplitude;

  float d = -uTime * 0.5 * uSpeed;
  float a = 0.0;
  for (float i = 0.0; i < 8.0; ++i) {
    a += cos(i - d - a * uv.x);
    d += sin(uv.y * i + a);
  }
  d += uTime * 0.5 * uSpeed;
  vec3 col = vec3(cos(uv * vec2(d, a)) * 0.6 + 0.4, cos(a + d) * 0.5 + 0.5);
  col = cos(col * cos(vec3(d, a, 2.5)) * 0.5 + 0.5) * uColor;
  gl_FragColor = vec4(col, 1.0);
}
`;

@Component({
  selector: 'nc-iridescence-background',
  templateUrl: './iridescence-background.component.html',
  styleUrls: ['./iridescence-background.component.less']
})
export class IridescenceBackgroundComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLDivElement>;

  /** 颜色 [r, g, b] 数组（0-1） */
  @Input() ncColor: number[] = [0.5, 0.6, 0.8];
  /** 动画速度倍率（0-2） */
  @Input() ncSpeed: number = 1.0;
  /** 鼠标驱动效应的振幅 */
  @Input() ncAmplitude: number = 0.1;
  /** 是否响应鼠标操作 */
  private _mouseReact: boolean = true;
  @Input()
  set ncMouseReact(val: boolean | string) {
    this._mouseReact = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncMouseReact(): boolean {
    return this._mouseReact;
  }

  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.Camera | null = null;
  private material: THREE.RawShaderMaterial | null = null;
  private mesh: THREE.Mesh | null = null;

  private rafId: number | null = null;
  private resizeRafId: number | null = null;
  private resizeObserver!: ResizeObserver;
  private intersectionObserver!: IntersectionObserver;
  private isVisible = true;
  private mousePos = new THREE.Vector2(0.5, 0.5);

  constructor(private ngZone: NgZone) { }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    this.initWebGL();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.material) return;

    if (changes['ncColor']) {
      this.material.uniforms['uColor'].value.setRGB(this.ncColor[0], this.ncColor[1], this.ncColor[2]);
    }
    if (changes['ncSpeed']) {
      this.material.uniforms['uSpeed'].value = this.ncSpeed;
    }
    if (changes['ncAmplitude']) {
      this.material.uniforms['uAmplitude'].value = this.ncAmplitude;
    }
  }

  // ─── 初始化 WebGL ──────────────────────────────────────────────────────────

  private initWebGL(): void {
    const container = this.containerRef.nativeElement;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setClearColor(0xffffff, 1);
    // 限定最大 dpr，避免在超高分辨率屏幕上性能过低
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.camera = new THREE.Camera(); // 空相机，不使用投影矩阵

    const geometry = new THREE.PlaneGeometry(2, 2);
    this.material = new THREE.RawShaderMaterial({
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(this.ncColor[0], this.ncColor[1], this.ncColor[2]) },
        uResolution: { value: new THREE.Vector3() },
        uMouse: { value: this.mousePos },
        uAmplitude: { value: this.ncAmplitude },
        uSpeed: { value: this.ncSpeed }
      }
    });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.scene.add(this.mesh);

    this.setupResizeObserver(container);
    this.setupIntersectionObserver(container);

    this.resize();
    this.ngZone.runOutsideAngular(() => {
      this.startAnimation();
    });
  }

  // ─── Resize 处理 ──────────────────────────────────────────────────────────

  private resize(): void {
    const container = this.containerRef.nativeElement;
    if (!container || !this.renderer || !this.material) return;

    const width = container.offsetWidth;
    const height = container.offsetHeight;

    this.renderer.setSize(width, height);

    const canvas = this.renderer.domElement;
    this.material.uniforms['uResolution'].value.set(
      canvas.width,
      canvas.height,
      canvas.width / canvas.height
    );
  }

  private setupResizeObserver(container: HTMLElement): void {
    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeRafId !== null) cancelAnimationFrame(this.resizeRafId);
      this.ngZone.runOutsideAngular(() => {
        this.resizeRafId = requestAnimationFrame(() => {
          this.resize();
          this.resizeRafId = null;
        });
      });
    });
    this.resizeObserver.observe(container);
  }

  // ─── 渲染循环 ─────────────────────────────────────────────────────────────

  private startAnimation(): void {
    const update = (t: number) => {
      this.rafId = requestAnimationFrame(update);
      if (this.isVisible && this.renderer && this.scene && this.camera && this.material) {
        this.material.uniforms['uTime'].value = t * 0.001;
        this.renderer.render(this.scene, this.camera);
      }
    };
    update(performance.now());
  }

  private stopAnimation(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  // ─── IntersectionObserver：不可见时暂停渲染以节省性能 ────────────────────

  private setupIntersectionObserver(container: HTMLElement): void {
    this.intersectionObserver = new IntersectionObserver(entries => {
      const entry = entries[0];
      this.isVisible = entry.isIntersecting && entry.intersectionRatio > 0;
    }, { threshold: [0, 0.01, 0.1] });
    this.intersectionObserver.observe(container);
  }

  // ─── 清理资源 ─────────────────────────────────────────────────────────────

  private cleanup(): void {
    this.stopAnimation();

    if (this.resizeRafId !== null) {
      cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = null;
    }
    try { this.resizeObserver?.disconnect(); } catch (e) { void 0; }
    try { this.intersectionObserver?.disconnect(); } catch (e) { void 0; }

    if (this.renderer) {
      const canvas = this.renderer.domElement;
      if (canvas && canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
      this.renderer.dispose();
    }

    if (this.material) {
      this.material.dispose();
    }
    if (this.mesh && this.mesh.geometry) {
      this.mesh.geometry.dispose();
    }

    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.material = null;
    this.mesh = null;
  }

  // ─── HostListeners for Mouse Interaction ─────────────────────────────────

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.ncMouseReact || !this.material || !this.containerRef) return;
    const container = this.containerRef.nativeElement;
    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    // 检查是否在容器内
    if (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    ) {
      return;
    }

    const x = (event.clientX - rect.left) / rect.width;
    const y = 1.0 - (event.clientY - rect.top) / rect.height;

    // 更新 THREE.Vector2
    this.mousePos.set(x, y);
  }

  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (!this.ncMouseReact || !this.material || !this.containerRef || event.touches.length === 0) return;
    const container = this.containerRef.nativeElement;
    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const touch = event.touches[0];

    // 检查是否在容器内
    if (
      touch.clientX < rect.left ||
      touch.clientX > rect.right ||
      touch.clientY < rect.top ||
      touch.clientY > rect.bottom
    ) {
      return;
    }

    const x = (touch.clientX - rect.left) / rect.width;
    const y = 1.0 - (touch.clientY - rect.top) / rect.height;

    // 更新 THREE.Vector2
    this.mousePos.set(x, y);
  }
}
